import { Link, useParams } from 'wouter';
import {
  InputDropdownItem,
  FormsInputDropdown,
  FormsCheckBox,
  InlineFormInput,
  DatePicker,
  Button,
  Spinner,
  IconButton,
} from '@platform/components';
import { AdminIntroductions } from '@platform/client';
import { DeclineReasons } from '../../../dashboard/my-projects/Modals/IntroductionModal';
import {
  IntroductionOutcomeEnum,
  DiscoveryDecision,
  AssignmentDeclinedReason,
  IntroductionUpdateResponse,
  AccountTypeEnum,
} from '@platform/client/schemas';
import { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { snakeToTitleCase } from '../../../util/TitleCase';
import {
  Control,
  Controller,
  FormState,
  SubmitHandler,
  useForm,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import { AdminTableContext } from '../../AdminTable';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';

export const IntroductionUpdate = () => {
  const { introductionId } = useParams<{ introductionId: string }>();
  const { data: introduction } = AdminIntroductions.useGetIntroductionById(
    parseInt(introductionId),
  );

  if (!introduction) {
    return <div className='text-center'>Loading...</div>;
  }

  return (
    <div className='md:flex gap-6 w-full md:w-[900px] md:mx-auto'>
      {introduction && <IntroDetailSection introduction={introduction} />}
      <hr className='my-4 md:none' />
      {introduction && <IntroUpdateForm introduction={introduction} />}
    </div>
  );
};

const IntroDetail = ({ title, value }: { title: string; value: string | null }) => {
  return (
    <div className='md:inline-flex'>
      <p className='font-semibold min-w-20'>{title}</p>
      <p className='text-gray-500'>{value}</p>
    </div>
  );
};

const IntroDetailSection = ({ introduction }: { introduction: IntroductionUpdateResponse }) => {
  const orgRepText = `${introduction.representativeName} (${introduction.organizationName})`;
  const sentByText =
    introduction.sentByUserType === AccountTypeEnum.experteer ?
      introduction.professionalName
    : orgRepText;
  const recipText =
    introduction.sentByUserType === AccountTypeEnum.experteer ?
      orgRepText
    : introduction.professionalName;
  return (
    <div className='flex flex-col gap-4 md:w-[80%]'>
      <IntroDetail title='From' value={sentByText} />
      <IntroDetail title='To' value={recipText} />
      {introduction.projectName && <IntroDetail title='Project' value={introduction.projectName} />}
      <div className='border-1 italic rounded-sm border-gray-200 p-4'>{`"${introduction.message}"`}</div>
    </div>
  );
};

type MatchUpdateFormData = {
  introductionId: number;
  introOutcome: IntroductionOutcomeEnum | null;
  introReasonId: string | null;
  proDiscoveryDecision: DiscoveryDecision | null;
  proDiscoveryReason: AssignmentDeclinedReason | null;
  repDiscoveryDecision: DiscoveryDecision | null;
  repDiscoveryReason: AssignmentDeclinedReason | null;
  projectName: string | null;
  projectStartDate: Date | null;
  projectEndDate: Date | null;
  sendEmail: boolean | null;
};

type FormStateType = 'form' | 'submitting' | 'error' | 'success' | 'delete';
const IntroUpdateForm = (props: { introduction: IntroductionUpdateResponse }) => {
  const [formStage, setFormStage] = useState<FormStateType>('form');
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { introduction } = props;
  const { control, handleSubmit, watch, formState, register } = useForm<MatchUpdateFormData>({
    values: {
      introductionId: introduction.introductionId,
      introOutcome: introduction.outcome,
      introReasonId: introduction.introReasonId ? introduction.introReasonId.toString() : null,
      proDiscoveryDecision: introduction.proDiscoveryDecision,
      proDiscoveryReason: introduction.proDiscoveryReason,
      repDiscoveryDecision: introduction.repDiscoveryDecision,
      repDiscoveryReason: introduction.repDiscoveryReason,
      projectName: introduction.projectName,
      projectStartDate:
        introduction.projectStartDate ? new Date(introduction.projectStartDate) : null,
      projectEndDate: introduction.projectEndDate ? new Date(introduction.projectEndDate) : null,
      sendEmail: false,
    },
  });

  const selectedIntroOutcome = watch('introOutcome');
  const proDiscoveryDecision = watch('proDiscoveryDecision');
  const isMatch =
    proDiscoveryDecision === DiscoveryDecision.accepted &&
    selectedIntroOutcome === IntroductionOutcomeEnum.accepted;

  const queryClient = useQueryClient();
  const updateOutcome = AdminIntroductions.useUpdateMyProjectStatus({
    mutation: {
      onMutate: () => {
        setFormStage('submitting');
      },
      onError: () => {
        setFormStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminIntroductions.getGetIntroductionsQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
        setFormStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<MatchUpdateFormData> = (data) => {
    const startDate = data.projectStartDate ? data.projectStartDate.toISOString() : null;
    const endDate = data.projectEndDate ? data.projectEndDate.toISOString() : null;
    updateOutcome.mutate({
      data: {
        introductionId: props.introduction.introductionId,
        introductionOutcome: data.introOutcome as IntroductionOutcomeEnum,
        introductionReasonId: data.introReasonId ? parseInt(data.introReasonId) : null,
        discoveryProDecision: data.proDiscoveryDecision ?? null,
        discoveryProReason: data.proDiscoveryReason ?? null,
        discoveryRepDecision: data.repDiscoveryDecision ?? null,
        discoveryRepReason: data.repDiscoveryReason ?? null,
        projectName: data.projectName ?? null,
        projectStartDate: startDate,
        projectEndDate: endDate,
        sendEmail: data.sendEmail,
      },
    });
  };

  return (
    <div className='w-full md:w-[60%] md:mx-auto md:h-[480px]'>
      {formStage === 'submitting' && <UpdateSubmitting />}
      {formStage === 'error' && <UpdateError />}
      {formStage === 'success' && <UpdateSuccess />}
      {formStage === 'delete' && (
        <DeleteIntroduction
          introductionId={props.introduction.introductionId}
          setFormStage={setFormStage}
        />
      )}
      {(formStage === 'error' || formStage === 'success') && <BackToIntrosButton />}
      {formStage === 'form' && (
        <form className='flex flex-col gap-2 h-full'>
          <div className='flex justify-between'>
            <Button variant='secondary-gray' href='/' size='sm'>
              Cancel
            </Button>
            <div className='flex gap-4'>
              <IconButton
                aria-label='Delete Introduction'
                size='sm'
                variant='secondary-danger'
                icon='trash-01'
                onPress={() => setFormStage('delete')}
              />

              <Button variant='primary' size='sm' onPress={() => handleSubmit(onSubmit)()}>
                Update match
              </Button>
            </div>
          </div>
          <IntroOutcomeDropdown control={control} formState={formState} watch={watch} />
          {selectedIntroOutcome === IntroductionOutcomeEnum.accepted && (
            <DiscoveryDecisionDropdown control={control} formState={formState} watch={watch} />
          )}
          {isMatch && (
            <ProjectDetails
              customProject={!introduction?.projectId}
              register={register}
              watch={watch}
              formState={formState}
              control={control}
            />
          )}
          <FormsCheckBox
            size='md'
            control={control}
            name='sendEmail'
            aria-label='send email'
            description='Send email notification'
          />
        </form>
      )}
    </div>
  );
};

const introDropdownItemsOutcomes: InputDropdownItem[] = Object.values(IntroductionOutcomeEnum).map(
  (outcome) => ({
    text: snakeToTitleCase(outcome),
    textValue: outcome,
  }),
);

const introDropdownItemsReasons: InputDropdownItem[] = Object.entries(DeclineReasons).map(
  ([reason, id]) => ({
    text: snakeToTitleCase(reason),
    textValue: id,
  }),
);

const IntroOutcomeDropdown = (props: {
  control: Control<MatchUpdateFormData>;
  formState: FormState<MatchUpdateFormData>;
  watch: UseFormWatch<MatchUpdateFormData>;
}) => {
  const selectedOutcome = props.watch('introOutcome');
  return (
    <>
      <FormsInputDropdown
        type='default'
        control={props.control}
        name='introOutcome'
        label='Intro outcome'
        items={introDropdownItemsOutcomes}
        inputDisabled
        error={props.formState.errors.introOutcome}
      />
      {selectedOutcome === IntroductionOutcomeEnum.declined && (
        <FormsInputDropdown
          type='default'
          control={props.control}
          name='introReasonId'
          label='Reason'
          items={introDropdownItemsReasons}
          inputDisabled
          rules={{ required: 'Decline reason is required' }}
          error={props.formState.errors.introReasonId}
        />
      )}
    </>
  );
};

const decisionItems = Object.values(DiscoveryDecision).map((decision) => ({
  text: snakeToTitleCase(decision),
  textValue: decision,
}));
const reasonsItems = Object.values(AssignmentDeclinedReason).map((reason) => ({
  text: snakeToTitleCase(reason),
  textValue: reason,
}));

const DiscoveryDecisionDropdown = (props: {
  control: Control<MatchUpdateFormData>;
  formState: FormState<MatchUpdateFormData>;
  watch: UseFormWatch<MatchUpdateFormData>;
}) => {
  const currentProDecision = props.watch('proDiscoveryDecision');
  const currentRepDecision = props.watch('repDiscoveryDecision');
  return (
    <div>
      <FormsInputDropdown
        type='default'
        control={props.control}
        name='proDiscoveryDecision'
        label='Professional discovery decision'
        items={decisionItems.map((item) => {
          return {
            ...item,
            text:
              item.textValue === DiscoveryDecision.unresponsive ? 'Org is unresponsive' : item.text,
          };
        })}
        inputDisabled
        rules={{ required: 'Professional decision is required' }}
        error={props.formState.errors.proDiscoveryDecision}
      />
      {currentProDecision === DiscoveryDecision.declined && (
        <FormsInputDropdown
          type='default'
          control={props.control}
          name='proDiscoveryReason'
          label='Professional reason'
          items={reasonsItems.filter(
            (reasonItem) => reasonItem.textValue !== 'choosing_different_pro',
          )}
          inputDisabled
        />
      )}
      {currentProDecision && currentProDecision === DiscoveryDecision.pending && (
        <div>
          <FormsInputDropdown
            type='default'
            control={props.control}
            name='repDiscoveryDecision'
            label='Representative discovery decision'
            items={decisionItems.map((item) => {
              return {
                ...item,
                text:
                  item.textValue === DiscoveryDecision.unresponsive ?
                    'Pro is unresponsive'
                  : item.text,
              };
            })}
            inputDisabled
            rules={{ required: 'Representative decision is required' }}
            error={props.formState.errors.repDiscoveryDecision}
          />
          {currentRepDecision === DiscoveryDecision.declined && (
            <FormsInputDropdown
              type='default'
              control={props.control}
              name='repDiscoveryReason'
              label='Representative reason'
              items={reasonsItems.filter(
                (reasonItem) => reasonItem.textValue !== 'choosing_different_org',
              )}
              inputDisabled
            />
          )}
        </div>
      )}
    </div>
  );
};

const ProjectDetails = (props: {
  customProject: boolean;
  control: Control<MatchUpdateFormData>;
  register: UseFormRegister<MatchUpdateFormData>;
  formState: FormState<MatchUpdateFormData>;
  watch: UseFormWatch<MatchUpdateFormData>;
}) => {
  return (
    <div className='flex flex-col gap-4'>
      <p>{`🎉 It's a match! Provide the project ${props.customProject ? 'name and' : ''} dates below.`}</p>
      {props.customProject && (
        <InlineFormInput
          register={() => props.register('projectName', { required: 'Project name is required' })}
          label='Project Name'
          placeholder='Enter project name'
          errors={props.formState.errors.projectName}
        />
      )}
      <div className='flex gap-6'>
        <Controller
          control={props.control}
          name='projectStartDate'
          rules={{ required: 'Project start date is required' }}
          render={({ field }) => (
            <DatePicker label='Start' {...field} initialDate={field.value ?? undefined} />
          )}
        />
        <Controller
          control={props.control}
          name='projectEndDate'
          rules={{ required: 'Project end date is required' }}
          render={({ field }) => (
            <DatePicker label='End' {...field} initialDate={field.value ?? undefined} />
          )}
        />
      </div>
      {(props.formState.errors.projectStartDate || props.formState.errors.projectEndDate) && (
        <p className='text-sm text-error-500'>Project start and date required</p>
      )}
    </div>
  );
};

const DeleteIntroduction = ({
  introductionId,
  setFormStage,
}: {
  introductionId: number;
  setFormStage: (stage: FormStateType) => void;
}) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const queryClient = useQueryClient();
  const deleteIntroMutator = AdminIntroductions.useDeleteIntroduction({
    mutation: {
      onError: () => {
        setFormStage('error');
      },
      onSuccess: () => {
        setFormStage('success');
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminIntroductions.getGetIntroductionsQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
      },
    },
  });
  return (
    <div className='text-center pt-8'>
      <p className='mb-2'>
        Are you sure you want to delete this introduction? All related assignments and discoveries
        will also be deleted.
      </p>
      <p> This action cannot be undone.</p>
      <div className='flex gap-4 mt-4 justify-center'>
        <Button variant='secondary-gray' size='sm' onPress={() => setFormStage('form')}>
          Cancel
        </Button>
        <Button
          variant='primary-danger'
          size='sm'
          onPress={() => deleteIntroMutator.mutate({ data: { introductionId: introductionId } })}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

const UpdateSuccess = () => {
  return (
    <div className='flex flex-col items-center gap-4'>
      <GreenCircleCheckmark />
      <p className='text-lg text-center'>The introduction has been updated.</p>
    </div>
  );
};

const UpdateSubmitting = () => {
  return (
    <div className='flex flex-col items-center gap-4'>
      <Spinner />
      <p className='text-lg'>Submitting your changes...</p>
    </div>
  );
};

const UpdateError = () => {
  return (
    <p className='flex flex-col items-center gap-4 text-center'>
      An error occurred while updating the introduction, please try again later or contact support.
    </p>
  );
};

const BackToIntrosButton = () => {
  return (
    <div className='w-full text-center mt-2'>
      <Link className='text-brand-500 hover:underline' href='/'>
        Back to introductions
      </Link>
    </div>
  );
};
